import { useCallback, useEffect, useRef } from "react";
import { useLocalStorageState } from "@datapay-by-datacom/design-system";

export const MIN_WIDTH = 250;
export const MAX_WIDTH = 600;
export const DEFAULT_WIDTH = 300;
export const COLLAPSE_THRESHOLD = MIN_WIDTH - 50;

export function useSidebarResize() {
  const [isCollapsed, setIsCollapsed] = useLocalStorageState<boolean>(
    "sidebarIsCollapsed",
    true
  );
  const [sidebarWidth, setSidebarWidth] = useLocalStorageState<number>(
    "sidebarWidth",
    DEFAULT_WIDTH
  );
  const isDragging = useRef(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    return () => {
      cleanupRef.current?.();
    };
  }, []);

  const onResizeStart = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      isDragging.current = true;

      document.body.style.userSelect = "none";
      document.body.style.cursor = "col-resize";

      const cleanup = () => {
        isDragging.current = false;
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
        document.removeEventListener("pointermove", onPointerMove);
        document.removeEventListener("pointerup", onPointerUp);
        cleanupRef.current = null;
      };

      const onPointerMove = (moveEvent: PointerEvent) => {
        if (!isDragging.current) return;

        const newWidth = moveEvent.clientX;

        if (newWidth < COLLAPSE_THRESHOLD) {
          setIsCollapsed(true);
          cleanup();
          return;
        }

        const clampedWidth = Math.min(Math.max(newWidth, MIN_WIDTH), MAX_WIDTH);

        if (sidebarRef.current) {
          sidebarRef.current.style.width = `${clampedWidth}px`;
        }

        setSidebarWidth(clampedWidth);
      };

      const onPointerUp = () => {
        cleanup();
      };

      cleanupRef.current = cleanup;
      document.addEventListener("pointermove", onPointerMove);
      document.addEventListener("pointerup", onPointerUp);
    },
    [setIsCollapsed, setSidebarWidth]
  );

  const toggleSidebar = useCallback(() => {
    setIsCollapsed(!isCollapsed);
  }, [isCollapsed, setIsCollapsed]);

  return {
    isCollapsed,
    sidebarWidth: isCollapsed ? 0 : sidebarWidth,
    isDragging,
    sidebarRef,
    toggleSidebar,
    onResizeStart,
  };
}
